import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { ClientSafeHandoffViewV1Schema } from "@enrein/shared-contracts";
import { toClientApiErrorMessage } from "@enrein/shared-contracts/display/tier1-client-display";
import type { GreenfieldClientHubProps } from "../types.js";

export function GreenfieldRecordsView({
  active,
  api,
  linkId,
  token,
  proofSlot,
}: {
  readonly active: boolean;
  readonly api: GreenfieldClientHubProps["api"];
  readonly linkId: string;
  readonly token: string;
  readonly proofSlot?: ReactNode;
}) {
  const [error, setError] = useState<string | null>(null);
  const [downloadAvailable, setDownloadAvailable] = useState(false);
  const [statusLabel, setStatusLabel] = useState("Preparing your ownership package");

  const load = useCallback(async () => {
    if (!active) return;
    try {
      const view = ClientSafeHandoffViewV1Schema.parse(await api.getHandoff(linkId, token));
      setDownloadAvailable(view.downloadAvailable);
      setStatusLabel(view.downloadAvailable ? "Ownership package is ready" : "Ownership package is not ready yet");
      setError(null);
    } catch (err) {
      setDownloadAvailable(false);
      setError(err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)));
    }
  }, [active, api, linkId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function download() {
    try {
      const blob = await api.downloadHandoff(linkId, token);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "ownership-package.zip";
      link.rel = "noreferrer";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)));
    }
  }

  if (!active) return null;

  return (
    <section className="ws-card" id="gf-tab-records" role="tabpanel" aria-labelledby="gf-tab-btn-records">
      <h2 className="ws-title">Records</h2>
      <p id="gf-ownership-reason">{statusLabel}</p>
      {error ? <p className="ws-muted">{error}</p> : null}
      <button
        type="button"
        className="ws-btn ws-btn-primary"
        disabled={!downloadAvailable}
        aria-describedby="gf-ownership-reason"
        data-testid="download-ownership-package"
        onClick={() => void download()}
      >
        Download ownership package
      </button>
      {proofSlot ? <div style={{ marginTop: 24 }}>{proofSlot}</div> : null}
    </section>
  );
}

export function GreenfieldHelpView({
  active,
  agencyDisplayName,
  helpLabel,
}: {
  readonly active: boolean;
  readonly agencyDisplayName: string;
  readonly helpLabel: string;
}) {
  if (!active) return null;
  return (
    <section className="ws-card" id="gf-tab-help" role="tabpanel" aria-labelledby="gf-tab-btn-help">
      <h2 className="ws-title">{helpLabel}</h2>
      <p>
        {agencyDisplayName} remains responsible for this project. If you are stuck, contact your agency. This
        workspace does not invent a second support path.
      </p>
    </section>
  );
}
